import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Bell, Mail, FileText, MessageSquare, Home, Settings } from 'lucide-react';
import { PushNotificationToggle } from './PushNotificationToggle';

interface NotificationPrefs {
  push_enabled: boolean;
  email_enabled: boolean;
  claims_notifications: boolean;
  messages_notifications: boolean;
  placements_notifications: boolean;
  system_notifications: boolean;
}

export function NotificationPreferences() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPrefs>({
    push_enabled: true,
    email_enabled: true,
    claims_notifications: true,
    messages_notifications: true,
    placements_notifications: true,
    system_notifications: true,
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPreferences({
          push_enabled: data.push_enabled,
          email_enabled: data.email_enabled,
          claims_notifications: data.claims_notifications,
          messages_notifications: data.messages_notifications,
          placements_notifications: data.placements_notifications,
          system_notifications: data.system_notifications,
        });
      } else {
        // Create default preferences
        await supabase.from('notification_preferences').insert({
          user_id: user.id,
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key: keyof NotificationPrefs, value: boolean) => {
    setSaving(true);
    const oldValue = preferences[key];
    
    // Optimistic update
    setPreferences(prev => ({ ...prev, [key]: value }));

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('notification_preferences')
        .update({ [key]: value })
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Preference updated",
        description: "Your notification settings have been saved.",
      });
    } catch (error) {
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: oldValue }));
      toast({
        title: "Error",
        description: "Failed to update preference. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Choose how and when you want to be notified
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Delivery Methods */}
        <div>
          <h3 className="text-sm font-medium mb-4">Delivery Methods</h3>
          <div className="space-y-4">
            <PushNotificationToggle />
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label htmlFor="email-notifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive important updates via email
                  </p>
                </div>
              </div>
              <Switch
                id="email-notifications"
                checked={preferences.email_enabled}
                onCheckedChange={(checked) => updatePreference('email_enabled', checked)}
                disabled={saving}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Notification Types */}
        <div>
          <h3 className="text-sm font-medium mb-4">Notification Types</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label htmlFor="claims-notifications">Claims Updates</Label>
                  <p className="text-sm text-muted-foreground">
                    New claims, status changes, and claim-related activity
                  </p>
                </div>
              </div>
              <Switch
                id="claims-notifications"
                checked={preferences.claims_notifications}
                onCheckedChange={(checked) => updatePreference('claims_notifications', checked)}
                disabled={saving}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label htmlFor="messages-notifications">Messages</Label>
                  <p className="text-sm text-muted-foreground">
                    New messages and conversation updates
                  </p>
                </div>
              </div>
              <Switch
                id="messages-notifications"
                checked={preferences.messages_notifications}
                onCheckedChange={(checked) => updatePreference('messages_notifications', checked)}
                disabled={saving}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Home className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label htmlFor="placements-notifications">Placements</Label>
                  <p className="text-sm text-muted-foreground">
                    Housing placement updates and property matches
                  </p>
                </div>
              </div>
              <Switch
                id="placements-notifications"
                checked={preferences.placements_notifications}
                onCheckedChange={(checked) => updatePreference('placements_notifications', checked)}
                disabled={saving}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label htmlFor="system-notifications">System Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Account updates, security alerts, and announcements
                  </p>
                </div>
              </div>
              <Switch
                id="system-notifications"
                checked={preferences.system_notifications}
                onCheckedChange={(checked) => updatePreference('system_notifications', checked)}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
